import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Public soft-copy page, opened via QR scan. Rendered outside
// AppProvider/login (see App.jsx), so it uses its own standalone axios
// call instead of useApp — never redirects to login. Backend already
// excludes Stock ID/Transit ID/IMO number from the response.

// Same base-URL logic as utils/api.js, but a separate axios instance
// so this page doesn't depend on its JWT-attaching interceptor.
const PUBLIC_API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

export default function LabelSoftCopy({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${PUBLIC_API_BASE}/public/label/${token}`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.status === 404 ? 'This label link does not exist or has expired.' : 'Something went wrong, please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const wrapStyle = {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#f3f4f6', padding: 20, fontFamily: "'Inter', system-ui, sans-serif"
  };

  if (loading) {
    return <div style={wrapStyle}><p style={{ color: '#555' }}>Loading...</p></div>;
  }

  if (error || !data) {
    return (
      <div style={wrapStyle}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 380, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>⚠</div>
          <p style={{ color: '#374151', margin: 0 }}>{error || 'Label not found.'}</p>
        </div>
      </div>
    );
  }

  const entryDateDisplay = data.entry_date ? new Date(data.entry_date).toLocaleDateString('en-GB') : '—';

  return (
    <div style={wrapStyle}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 24, maxWidth: 420, width: '100%', boxShadow: '0 2px 16px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, borderBottom: '2px solid #111', paddingBottom: 12, marginBottom: 14 }}>
          <img src="/logo.png" alt="All In Logistics" style={{ width: 44, height: 44, objectFit: 'contain' }} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 17, color: '#111' }}>ALL IN LOGISTICS</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>Cargo Label — Soft Copy</div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Row label="Vessel" value={data.vessel || '—'} />
          <Row label="PO Number" value={data.po_numbers} mono />
          <Row label="Supplier" value={data.supplier || '—'} />
          <Row label="Entry Date" value={entryDateDisplay} />
          <Row label="Total Packages" value={data.quantity ?? '—'} />
          <Row label="Item Weights" value={`${data.item_weights || '—'} ${data.packaging_unit || 'KGS'}`} />
          <Row label="Dimensions" value={(data.dimensions || '—').replace(/x/gi, ' x ')} />
          <Row label="Warehouse Location" value={data.location || '—'} />
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
      <span style={{ color: '#6b7280', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</span>
      <span style={{ color: '#111', fontWeight: 600, fontFamily: mono ? "'JetBrains Mono',monospace" : 'inherit', textAlign: 'right' }}>{value}</span>
    </div>
  );
}
